import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalSearchParams } from 'expo-router';
import { ChatContext, MealType } from '@fytness/shared';
import { formatISO } from 'date-fns';
import { RightSidebar } from '~/layout/components/right-sidebar/RightSidebar';
import { RightSidebarTab } from '~/layout/contexts/right-sidebar-layout/RightSidebarLayout';
import { SidebarChat } from '~/chat/components/sidebar-chat/SidebarChat';
import { MealFavouritesPanel } from '~/meal/components/meal-favourites-panel/MealFavouritesPanel';
import { useCurrentOverviewDate } from '~/calendar/hooks/useCurrentOverviewDate';

const VALID_TYPES = new Set(Object.values(MealType));

/**
 * Right-sidebar rail for the meal log screen: an AI nutrition coach chat scoped
 * to the open day (date only — not the meal type, so switching sections keeps the
 * same conversation while switching days starts a fresh one), plus the user's
 * favourite (most-logged) meals. Renders nothing itself — `<RightSidebar>`
 * registers the tabs. The coach chat is available to everyone (Free users run it
 * against their monthly FYT credits; an empty wallet is handled at send time).
 *
 * The open day comes from the URL pathname via `useCurrentOverviewDate` — the
 * same source the layout's date picker uses. This sidebar is mounted in the
 * `(detail)` layout, ABOVE the `[date]` segment, so `useLocalSearchParams().date`
 * is out of scope here (it would fall back to today and never track the picker).
 */
export const MealLogSidebar = () => {
  const { t } = useTranslation('meal');
  const { type: typeParam } = useLocalSearchParams<{ type?: string }>();
  const currentDate = useCurrentOverviewDate();

  const { dateStr, mealType } = useMemo(() => {
    const date = formatISO(currentDate, { representation: 'date' });
    const upper = typeParam?.toUpperCase();
    const type =
      upper && VALID_TYPES.has(upper as MealType)
        ? (upper as MealType)
        : MealType.BREAKFAST;
    return { dateStr: date, mealType: type };
  }, [currentDate, typeParam]);

  const tabs = useMemo<RightSidebarTab[]>(
    () => [
      {
        key: 'coach',
        icon: 'Bot',
        label: t('rightSidebar.tabs.coach'),
        panel: (
          <SidebarChat
            context={ChatContext.MEAL}
            contextRefId={dateStr}
            title={t('rightSidebar.tabs.coach')}
            placeholder={t('rightSidebar.chat.placeholder')}
          />
        ),
      },
      {
        key: 'favourites',
        icon: 'Heart',
        label: t('rightSidebar.tabs.favourites'),
        panel: <MealFavouritesPanel date={dateStr} mealType={mealType} />,
      },
    ],
    [t, dateStr, mealType],
  );

  return <RightSidebar tabs={tabs} expandedWidth={340} />;
};
